import { styled } from '@mui/material/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  TaskEscalateReason,
  TaskResolveReason,
} from '../../../data/models/task-reason.model';
import ChipSelectMenu from '../../forms/ChipSelectMenu';

const PREFIX = 'TaskDialog';

const classes = {
  content: `${PREFIX}-content`,
  actions: `${PREFIX}-actions`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
    width: 500,
  },

  [`& .${classes.actions}`]: {
    gap: theme.spacing(1),
  },
}));

interface TaskDialogProps {
  title: string;
  message: string;
  reasons: TaskEscalateReason[] | TaskResolveReason[];
  reasonBCAttribute: string;
  reasonNameAttribute: string;
  open: boolean;
  close: (result: { ReasonBCs: string[] } | null) => void;
}

export default function TaskDialog({
  title,
  message,
  reasons,
  reasonBCAttribute,
  reasonNameAttribute,
  open,
  close,
}: TaskDialogProps) {
  const { t } = useTranslation('common');
  const requiredFieldMessage = t('common.requiredField');

  const formHook = useForm({
    mode: 'onTouched',
    defaultValues: { ReasonBCs: [] },
  });

  const handleClose = () => {
    formHook.reset();
    close(null);
  };

  const handleSave = (data: any) => {
    formHook.reset();
    close({ ReasonBCs: data.ReasonBCs });
  };

  return (
    <RootDialog open={open} onClose={handleClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <div className={classes.content}>
          <Typography>{message}</Typography>
          <ChipSelectMenu
            label={t('workQueue.selectReason')}
            name="ReasonBCs"
            values={reasons}
            valueAttr={reasonBCAttribute}
            labelAttr={reasonNameAttribute}
            required={true}
            control={formHook.control}
            rules={{ required: requiredFieldMessage }}
          />
        </div>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant="contained" color="secondary" onClick={handleClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!formHook.formState.isValid}
          onClick={formHook.handleSubmit(handleSave)}>
          {t('common.ok')}
        </Button>
      </DialogActions>
    </RootDialog>
  );
}
